import { useState, useEffect } from "react";
import { technicalAnalysisApi } from "../api/technicalAnalysisApi";
import { ITechAnalytics } from '../types';
import { TimeFrames } from '../../types';


const formatData = (techAnalysisData: ITechAnalytics) => {
    const {
        scores,
        technicalIndicatorsAnalysis,
        pivotPointsAnalysis,
        movingAveragesAnalysis,
        date,
        ticker
    } = techAnalysisData;

    const { exponential, simple } = movingAveragesAnalysis;
    const { classic, fibonacci } = pivotPointsAnalysis;

    return {
        date,
        ticker,
        technicalSentiment: {
            summary: {
                buy: scores.summaryScore.buy,
                neutral: scores.summaryScore.neutral,
                sell: scores.summaryScore.sell,
                scoreScale: scores.summaryScore.scoreScale,

            },
            oscillators: {
                buy: scores.oscillatorsScore.buy,
                neutral: scores.oscillatorsScore.neutral,
                sell: scores.oscillatorsScore.sell,
                scoreScale: scores.oscillatorsScore.scoreScale,
            },
            movingAverages: {
                buy: scores.movingAveragesScore.buy,
                neutral: scores.movingAveragesScore.neutral,
                sell: scores.movingAveragesScore.sell,
                scoreScale: scores.movingAveragesScore.scoreScale,
            },
        },
        indicators: Object.keys(technicalIndicatorsAnalysis).map(key => ({
            name: technicalIndicatorsAnalysis[key].name,
            value: (technicalIndicatorsAnalysis[key].score),
            action: technicalIndicatorsAnalysis[key].indicator || null,
        })),
        pivotPoints: [
            {
                name: "Classic",
                s3: classic.s3.score,
                s2: classic.s2.score,
                s1: classic.s1.score,
                pivotPoints: classic.pivotPoint.score,
                r1: classic.r1.score,
                r2: classic.r2.score,
                r3: classic.r3.score,
            },
            {
                name: "Fibonacci",
                s3: fibonacci.s3.score,
                s2: fibonacci.s2.score,
                s1: fibonacci.s1.score,
                pivotPoints: fibonacci.pivotPoint.score,
                r1: fibonacci.r1.score,
                r2: fibonacci.r2.score,
                r3: fibonacci.r3.score,
            },
        ],
        movingAverages: [
            {
                period: "5",
                simple: simple?.mA5?.score,
                exponential: exponential?.mA5?.score,
                action: simple?.mA5?.indicator || null,
            },
            {
                period: "10",
                simple: simple?.mA10?.score,
                exponential: exponential?.mA10?.score,
                action: simple?.mA10?.indicator || null,
            },
            {
                period: "20",
                simple: simple?.mA20?.score,
                exponential: exponential?.mA20?.score,
                action: simple?.mA20?.indicator || null,
            },
            {
                period: "50",
                simple: simple?.mA50?.score,
                exponential: exponential?.mA50?.score,
                action: simple?.mA50?.indicator || null,
            },
            {
                period: "100",
                simple: simple?.mA100?.score,
                exponential: exponential?.mA100?.score,
                action: simple?.mA100?.indicator || null,
            },
        ],
        technicalIndicatorsAnalysis
    };
};

export type TechnicalAnalysisData = ReturnType<typeof formatData>;

const getTimeframeValue = (timeFrame: TimeFrames): string => {
    switch (timeFrame) {
        case TimeFrames.D1:
            return "day";
        case TimeFrames.MON1:
            return "month";
        case TimeFrames.W1:
            return "week";
        case TimeFrames.W2:
            return "twoWeeks";
        case TimeFrames.D3:
            return "threeDays";
        default:
            return timeFrame;
    }
};

export const useSymbolTechnicalAnalysis = ({
    isActive,
    ticker,
}: {
    isActive: boolean;
    ticker: string;
}) => {

    const [timeFrame, setTimeFrame] = useState<TimeFrames>(TimeFrames.D1);
    const [data, setData] = useState<Record<string, TechnicalAnalysisData> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const cacheKey = `${ticker}-${timeFrame}`;

    useEffect(() => {

        if (!isActive || !ticker) return;

        if (data?.[cacheKey] !== undefined) return;

        const fetchData = async () => {

            try {
                setIsLoading(true);
                const techAnalysisData = await technicalAnalysisApi.getTechnicalAnalysisData({
                    ticker,
                    timeFrame: getTimeframeValue(timeFrame)
                });

                if (!techAnalysisData || !techAnalysisData?.length) return;
                //@ts-ignore   
                setData(prev => ({
                    ...(prev || {}),
                    [cacheKey]: techAnalysisData,
                }));
            } catch (error) {
                console.error("Failed to fetch technical analysis data: ", error);
                return;
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [isActive, ticker, timeFrame]);

    return {
        data: data?.[cacheKey] || null,
        ticker,
        isLoading,
        timeFrame,
        setTimeFrame,
    };
};
